import { Row, Table } from '@tanstack/react-table'
import { clsx } from 'clsx'
import { TableCell, TableRow } from '@/ui/Table'
import { Disruption, DisruptionStatus } from '@/types/disruptions'
import { Role } from '@/types/auth'
import { useAuthStore } from '@/store/auth-store'
import { flexRender } from '@tanstack/react-table'
import styles from './DisruptionsTable.module.scss'
import { UpdateDisruptionForm } from './forms/UpdateDisruptionForm'
import { UseFormReturn } from 'react-hook-form'
import { DisruptionFormValues } from '../../types'
import { RefObject, useEffect, useRef } from 'react'
import { ROUTES } from '@/router'
import { useLocation } from 'react-router-dom'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import { useDisruptionsTableFormStateStore } from '../../store'

interface Props {
    row: Row<Disruption>
    table: Table<Disruption>
    handleRowClick: (row: Row<Disruption>) => void
    form?: UseFormReturn<DisruptionFormValues>
    editWrapperRef?: RefObject<HTMLTableRowElement>
}

export const DisruptionRow = ({ row, table, handleRowClick, form, editWrapperRef }: Props) => {
    const { role } = useAuthStore()
    const { pathname } = useLocation()
    const hasPermission = useCheckPermissions()
    const { draftCreatedReference, newlyCreatedReference } = useDisruptionsTableFormStateStore()

    const rowRef = useRef<HTMLTableRowElement>(null)
    const hasScrolledRef = useRef(false)

    useEffect(() => {
        if (newlyCreatedReference === row.original.reference && rowRef.current && !hasScrolledRef.current) {
            rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            hasScrolledRef.current = true
        }
        if (newlyCreatedReference !== row.original.reference) {
            hasScrolledRef.current = false
        }
    }, [newlyCreatedReference, row.original.reference])

    if (
        row.getIsSelected() &&
        pathname.includes(ROUTES.ALL_DISRUPTIONS) &&
        hasPermission('disruption', 'update') &&
        form &&
        editWrapperRef
    ) {
        return <UpdateDisruptionForm row={row} table={table} form={form} editWrapperRef={editWrapperRef} />
    }

    return (
        <TableRow
            ref={rowRef}
            data-disruption-id={row.original.id}
            onClick={() => handleRowClick(row)}
            className={clsx({
                [styles.selectedRow]: row?.getIsSelected(),
                [styles.newRow]: newlyCreatedReference === row.original.reference,
                [styles.draftRow]: draftCreatedReference === row.original.reference,
            })}
        >
            {row.getVisibleCells().map((cell) => (
                <TableCell
                    key={cell.id}
                    alignCenter={cell.column.columnDef.meta?.alignCenter}
                    className={clsx({
                        [styles.clearedValue]:
                            role === Role.Controller && row.original.status === DisruptionStatus.Cleared,
                    })}
                >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
            ))}
        </TableRow>
    )
}
